import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { authAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { CheckCircle2, Loader2 } from 'lucide-react';

export default function InviteRegister() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { register } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    authAPI.validateInvite(token)
      .then(r => { setInvite(r.data); setName(r.data.client_name || ''); })
      .catch(err => setError(err.response?.data?.detail || 'This invite link is invalid or has already been used.'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setSubmitting(true);
    try {
      await register({
        email: invite.email,
        password,
        name,
        phone,
        company,
        invite_token: token,
        role: 'client',
        user_type: 'client',
      });
      setSuccess(true);
      toast.success('Account created! Welcome to Canadian Truck Sales.');
      // Auto-redirect to portal after 2s
      setTimeout(() => navigate('/portal', { replace: true }), 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    }
    setSubmitting(false);
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#E31937] mx-auto mb-2" />
        <p className="text-gray-400 text-sm">Validating invite...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-testid="invite-error">
      <Card className="w-full max-w-sm bg-white border-gray-200 text-center">
        <CardContent className="pt-8 pb-6">
          <p className="text-red-500 font-medium mb-2">{error}</p>
          <p className="text-xs text-gray-400 mb-4">Please contact Canadian Truck Sales for a new invite.</p>
          <Button variant="outline" onClick={() => navigate('/login')} data-testid="go-to-login">Go to Login</Button>
        </CardContent>
      </Card>
    </div>
  );

  if (success) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-testid="register-success">
      <Card className="w-full max-w-sm bg-white border-gray-200 text-center">
        <CardContent className="pt-8 pb-6">
          <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-gray-900 mb-1">Registration Successful!</h2>
          <p className="text-sm text-gray-500 mb-1">Welcome to Canadian Truck Sales, {name}.</p>
          <p className="text-xs text-gray-400">Redirecting you to your portal...</p>
          <Loader2 className="w-4 h-4 animate-spin text-[#E31937] mx-auto mt-4" />
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-testid="invite-register-page">
      <Card className="w-full max-w-sm bg-white border-gray-200">
        <CardHeader className="text-center pb-4">
          <img src="https://canadiantrucksales.ca/wp-content/uploads/2025/01/CTS-Logo-1.svg" alt="CTS" className="h-12 mx-auto mb-3" />
          <CardTitle className="text-lg text-gray-900">Create Your Account</CardTitle>
          <p className="text-xs text-gray-400">You've been invited to the CTS Client Portal</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="text-xs text-gray-500 block mb-0.5">Email (from invite)</label>
              <Input value={invite.email} disabled className="bg-gray-200 border-gray-200 text-gray-600" data-testid="invite-email" />
            </div>
            <Input
              placeholder="Full Name"
              value={name}
              onChange={e => setName(e.target.value)}
              className="bg-gray-100 border-gray-200 text-gray-900"
              data-testid="invite-name"
              required
            />
            <Input
              type="password"
              placeholder="Password (min. 6 characters)"
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="bg-gray-100 border-gray-200 text-gray-900"
              data-testid="invite-password"
              required
            />
            <Input
              placeholder="Phone (optional)"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              className="bg-gray-100 border-gray-200 text-gray-900"
              data-testid="invite-phone"
            />
            <Input
              placeholder="Company (optional)"
              value={company}
              onChange={e => setCompany(e.target.value)}
              className="bg-gray-100 border-gray-200 text-gray-900"
              data-testid="invite-company"
            />
            <Button type="submit" className="w-full bg-[#E31937] hover:bg-[#C4122C] text-white" disabled={submitting} data-testid="invite-submit">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Creating Account...</> : 'Create Account'}
            </Button>
          </form>
          <p className="text-center text-xs text-gray-400 mt-3">
            Already have an account? <a href="/login" className="text-[#E31937] hover:underline">Sign in</a>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
